import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  IconButton,
  Popover,
  Badge,
  Tooltip,
} from '@mui/material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { activityCenterActions } from '../../../store/slices/activityCenterSlice';
import { isActiveStatus, isCompletedStatus } from '../../ui/ActivityCenterTasks/utils/activityCenter';
import ActivityCenterTasksPanel from '../../ActivityCenterTasksPanel';
import { useActivityCenterSignalR } from '../../../hooks/useActivityCenterSignalR';
import { equalsIgnoringCase } from '../../../utils/stringUtils';
import { useTranslation } from 'react-i18next';

const ActivityCenterPopover = () => {
  const { tasks } = useSelector((state) => state.activityCenter);
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState(null);
  const { t: translate } = useTranslation();

  const {
    subscribeToActivityTopic,
    unsubscribeFromActivityTopic,
    isConnected,
    updateActivityItem,
    completeActivityItem,
    failActivityItem
  } = useActivityCenterSignalR();

  useEffect(() => {
    if (isConnected) {
      const reportTopicConfig = {
        name: 'ReportGeneration',
        eventHandlers: [
          {
            eventName: 'ProcessOutputReleased',
            handler: (response) => {
              const { transactionLogID, status, ...rest } = response;
              if (equalsIgnoringCase(status, 'Success')) {
                completeActivityItem(`${transactionLogID}`, response);
              } else if (equalsIgnoringCase(status, 'Failed')) {
                failActivityItem(`${transactionLogID}`, rest.error || 'Error_ReportGenerationFailed');
              }
            },
            clientFilter: (data) => data.operation === 'Download'
          }
        ]
      };
      subscribeToActivityTopic(reportTopicConfig);

      dispatch(activityCenterActions.setConnectionStatus(true));
    } else {
      dispatch(activityCenterActions.setConnectionStatus(false));
    }

    return () => {
      if (isConnected) {
        unsubscribeFromActivityTopic('ReportGeneration');
      }
    };
  }, [
    isConnected,
    subscribeToActivityTopic,
    unsubscribeFromActivityTopic,
    dispatch,
    updateActivityItem,
    completeActivityItem,
    failActivityItem,
  ]);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClear = () => {
    dispatch(activityCenterActions.clearAllTasks());
  };

  const activeTasks = tasks.filter((task) => isActiveStatus(task.status));
  const hasCompletedTasks = tasks.some((task) => isCompletedStatus(task.status));

  return (
    <>
      <Tooltip title={translate('Tooltip_Activity')}>
        <IconButton onClick={handleOpen} color="primary" size="large">
          <Badge
            color="secondary"
            variant={activeTasks.length > 0 ? 'dot' : 'standard'}
            invisible={activeTasks.length === 0}
          >
            <AssignmentIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{
          mt: 1,
        }}
        slotProps={{
          paper: {
            sx: {
              width: 400
            }
          }
        }}
      >
        <ActivityCenterTasksPanel
          tasks={tasks}
          onClose={handleClose}
          onClear={handleClear}
          hasCompletedTasks={hasCompletedTasks}
        />
      </Popover>
    </>
  );
};

export default ActivityCenterPopover;
